import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { fetchDocumentAnalysis, getDocumentReportUrl, type DocumentAnalysisJob } from '../api/analysis';
import { ProgressPanel } from '../components/ProgressPanel';
import { ResultSummary } from '../components/ResultSummary';
import { SentenceHighlights } from '../components/SentenceHighlights';

const POLL_INTERVAL_MS = 1200;

export function ResultsPage() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const [job, setJob] = useState<DocumentAnalysisJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;
    let timeoutId: number | undefined;

    async function load() {
      if (!analysisId) {
        return;
      }
      try {
        const nextJob = await fetchDocumentAnalysis(analysisId);
        if (!isActive) {
          return;
        }
        setJob(nextJob);
        setError(null);
        if (nextJob.status === 'queued' || nextJob.status === 'processing') {
          timeoutId = window.setTimeout(load, POLL_INTERVAL_MS);
        }
      } catch (loadError) {
        if (!isActive) {
          return;
        }
        setError(loadError instanceof Error ? loadError.message : 'Unable to load analysis status.');
      }
    }

    load();

    return () => {
      isActive = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [analysisId]);

  const reportUrl = useMemo(() => (analysisId ? getDocumentReportUrl(analysisId) : '#'), [analysisId]);

  if (error) {
    return (
      <div className="rounded-[2rem] border border-rose-200 bg-rose-50 p-8 text-rose-700">
        <p className="text-xs font-semibold uppercase tracking-[0.25em]">Analysis error</p>
        <p className="mt-3 text-lg">{error}</p>
        <Link className="mt-5 inline-flex text-sm font-semibold text-rose-800 underline" to="/">
          Start a new upload
        </Link>
      </div>
    );
  }

  if (!job) {
    return <ProgressPanel status="queued" progress={5} />;
  }

  if (job.status === 'queued' || job.status === 'processing') {
    return (
      <div className="space-y-6">
        <ProgressPanel status={job.status} progress={job.progress} />
        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-slate-600">
            We are extracting text from <span className="font-semibold text-slate-900">{job.filename}</span> and
            calculating sentence-level evidence. This page refreshes automatically.
          </p>
        </div>
      </div>
    );
  }

  if (job.status === 'failed') {
    return (
      <div className="space-y-6">
        <ProgressPanel status={job.status} progress={job.progress} />
        <div className="rounded-[2rem] border border-rose-200 bg-white p-8 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-rose-700">Analysis failed</p>
          <h2 className="mt-3 text-3xl font-semibold text-slate-900">We could not analyze this document</h2>
          <p className="mt-3 text-base text-slate-600">{job.error ?? 'The backend returned a failure state.'}</p>
          <Link
            to="/"
            className="mt-6 inline-flex rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
          >
            Upload a different document
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ResultSummary filename={job.filename} result={job.result!} />
      <div className="flex flex-wrap gap-3">
        <a
          className="inline-flex rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
          href={reportUrl}
        >
          Download PDF report
        </a>
        <Link
          to="/"
          className="inline-flex rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-500"
        >
          Analyze a new document
        </Link>
      </div>
      <SentenceHighlights sentences={job.result!.sentences} />
    </div>
  );
}
